/**
 * feat-305 — Manager assistant: contract types.
 *
 * A 1:1 TypeScript mirror of ADR 0001 §1.4 (SSE event contract, v1) and §6.5
 * (conversation restore). This file is the single source both the real
 * streaming client (assistantService.streamChat) and the fake responder
 * (fakeAssistantData.fakeStreamChat) satisfy. Do not add page-specific fields
 * here: a new page adds a variant to `PageContext` and nothing else.
 */

/** Server Performance History page context (ADR §1.5 / §2). */
export interface SphPageContext {
  page: 'server_performance_history';
  tenant_id: string;
  server_id: string;
  location_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/** The union grows one variant per page (Scorecard adds `SscPageContext`). */
export type PageContext = SphPageContext;

/** A citation. `kind` is a discriminator; Scorecard will add `'server'`. */
export interface Footnote {
  n: number;
  kind: 'shift';
  ref_id: string;
  label: string;
}

export type StreamErrorCode =
  | 'limit_tool_calls'
  | 'timeout'
  | 'upstream'
  | 'refused'
  | 'incomplete'
  | 'privacy_blocked'
  | 'internal';

export type AssistantEvent =
  | {
      type: 'start';
      conversation_id: string;
      user_message_id: string;
      assistant_message_id: string;
      contract: 1;
    }
  | { type: 'status'; phase: 'looking_up' | 'writing'; label: string }
  | ({ type: 'footnote' } & Footnote)
  | { type: 'text'; delta: string }
  | {
      type: 'done';
      assistant_message_id: string;
      status: 'complete';
      footnotes: Footnote[];
    }
  | {
      type: 'error';
      code: StreamErrorCode;
      message: string;
      retryable: boolean;
      retry_after_s: number | null;
      assistant_message_id: string;
    };

/** Every event name the v1 contract defines. Anything else is ignored. */
export const ASSISTANT_EVENT_NAMES: ReadonlyArray<AssistantEvent['type']> = [
  'start',
  'status',
  'footnote',
  'text',
  'done',
  'error',
];

/**
 * Normalized client-side error: HTTP pre-stream failures (§1.4 table) and
 * in-stream `error` events collapse into this one shape. `network`,
 * `not_found`, `invalid`, `unauthorized` are client-side codes.
 */
export type AssistantErrorCode =
  | StreamErrorCode
  | 'limit_rate'
  | 'limit_busy'
  | 'limit_conversation'
  | 'not_found'
  | 'invalid'
  | 'network'
  | 'unauthorized';

export interface AssistantError {
  code: AssistantErrorCode;
  message: string;
  retryable: boolean;
  retryAfterS: number | null;
}

export type MessageStatus = 'complete' | 'streaming' | 'stopped' | 'error' | 'blocked';

/** Restore message shape: keys are exactly these (ADR §6.5). */
export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  body: string;
  status: MessageStatus;
  error_code: string | null;
  footnotes: Footnote[];
  created_at: string;
}

export interface AssistantConversation {
  id: string;
  created_at: string;
  updated_at: string;
  messages: AssistantMessage[];
}

export interface AssistantConfig {
  enabled: boolean;
  pages: string[];
}

export interface ChatRequest {
  conversation_id: string | null;
  page_context: PageContext;
  message: string;
}

/** Type guard used by the client so a thrown value can be told apart. */
export function isAssistantError(e: unknown): e is AssistantError {
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof (e as AssistantError).code === 'string' &&
    typeof (e as AssistantError).retryable === 'boolean'
  );
}
